/**
 * Light / dark mode toggle.
 *
 * Single source of truth: this zustand store + localStorage. useBranding
 * reads `theme` and, when dark, overrides the page background + surface
 * with dark equivalents while keeping the template's brand primary /
 * accent colors intact (so the bot still feels like its vertical).
 */

import { useEffect } from "react";
import { create } from "zustand";

export type Theme = "light" | "dark";

const STORAGE_KEY = "ai-leasing-os.theme";

function readInitial(): Theme {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(STORAGE_KEY) as Theme | null;
  if (stored === "light" || stored === "dark") return stored;
  // Sensible default: follow OS preference once on first load.
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

interface ThemeState {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

export const useTheme = create<ThemeState>((set, get) => ({
  theme: readInitial(),
  setTheme: (theme) => {
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, theme);
    set({ theme });
  },
  toggle: () => {
    const next: Theme = get().theme === "light" ? "dark" : "light";
    get().setTheme(next);
  },
}));

/**
 * Mount this once near the app root so the html element gets the
 * `data-theme` attribute set, which lets globals.css scope dark overrides.
 */
export function useApplyThemeAttribute(): void {
  const theme = useTheme((s) => s.theme);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
}
